/**
 * ModuleGate — route-content guard for the activation layer (mod-2).
 *
 * Wraps the page content (alongside BillingGate). If the current route belongs
 * to a module that isn't active for this org, it renders `ModuleOffPanel`
 * instead of the page — never a 404, never a dead end. Core routes and active
 * modules pass straight through.
 *
 * Pass-through while the org is still loading (currentOrg null) so a reload on a
 * module route never flashes the off-panel before the profile resolves.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { moduleOfPath } from '../config/modules';
import { useModules } from '../hooks/useModules';
import { useAuth } from '../hooks/useAuth';
// Drawn only when a page's module is off, so it rides its own chunk: the shell
// carries ModuleGate on every first visit and the first-load budget is full
// (350.1 KB of 350 on main, 9 Oct 2026).
const ModuleOffPanel = lazy(() => import('./ModuleOffPanel').then((m) => ({ default: m.ModuleOffPanel })));

export function ModuleGate({ currentRoute, children }: { currentRoute: string; children: ReactNode }) {
    const currentOrg = useAuth((s) => s.currentOrg);
    const { active } = useModules();

    // Not loaded yet → don't gate (avoids an off-panel flash before profile resolves).
    if (!currentOrg?.id) return <>{children}</>;

    const cls = moduleOfPath(currentRoute);
    // Unclassified route or core → always allowed. Active module → allowed.
    if (cls === undefined || cls === 'core' || active.has(cls)) return <>{children}</>;

    return <Suspense fallback={null}><ModuleOffPanel moduleId={cls} /></Suspense>;
}
